import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './Boutiques.css';

interface Boutique {
  id: number;
  nom: string;
  code: string;
  statut: string;
  adresse: string;
  ville: string;
  quartier: string;
  telephone: string;
  email: string;
  gerant: string;
  horaires: string;
  fondCaisse: number;
  couleur: string;
  mobileMoneyActif: boolean;
  creditActif: boolean;
  tvaActif: boolean;
}

const couleurs = [
  { val: '#185FA5', label: 'Bleu', cls: 'c-blue' },
  { val: '#1D9E75', label: 'Vert', cls: 'c-green' },
  { val: '#EF9F27', label: 'Orange', cls: 'c-orange' },
  { val: '#534AB7', label: 'Violet', cls: 'c-purple' },
  { val: '#1DC8E9', label: 'Cyan', cls: 'c-teal' },
  { val: '#E24B4A', label: 'Rouge', cls: 'c-red' },
];

const boutiqueVide = {
  nom: '', code: '', statut: 'active', type: 'Boutique de vente',
  adresse: '', ville: 'Ouagadougou', quartier: '', telephone: '',
  email: '', gerant: '', horaires: '', fondCaisse: 50000,
  couleur: '#185FA5', mobileMoneyActif: true, creditActif: true,
  tvaActif: true, remiseActif: false, fideliteActif: true, notes: ''
};

export default function Boutiques() {
  const [boutiques, setBoutiques] = useState<Boutique[]>([]);
  const [chargement, setChargement] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [boutiqueEnEdition, setBoutiqueEnEdition] = useState<number | null>(null);
  const [boutiqueASupprimer, setBoutiqueASupprimer] = useState<Boutique | null>(null);
  const [confirmNom, setConfirmNom] = useState('');
  const [form, setForm] = useState(boutiqueVide);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');

  useEffect(() => {
    chargerBoutiques();
  }, []);

  const chargerBoutiques = async () => {
    try {
      const res = await api.get('/boutiques');
      setBoutiques(res.data);
    } catch (e) {
      setErreur('Impossible de charger les boutiques');
    } finally {
      setChargement(false);
    }
  };

  const ouvrirCreation = () => {
    setBoutiqueEnEdition(null);
    setForm(boutiqueVide);
    setErreur('');
    setShowModal(true);
  };

  const ouvrirEdition = (b: Boutique) => {
    setBoutiqueEnEdition(b.id);
    setForm({ ...boutiqueVide, ...b });
    setErreur('');
    setShowModal(true);
  };

  const ouvrirSuppression = (b: Boutique) => {
    setBoutiqueASupprimer(b);
    setConfirmNom('');
    setShowDeleteModal(true);
  };

  const sauvegarder = async () => {
    if (!form.nom || !form.code || !form.adresse || !form.telephone) {
      setErreur('Nom, code, adresse et téléphone sont obligatoires');
      return;
    }
    try {
      if (boutiqueEnEdition) {
        await api.put(`/boutiques/${boutiqueEnEdition}`, form);
        setSucces('Boutique modifiée avec succès');
      } else {
        await api.post('/boutiques', form);
        setSucces('Boutique créée avec succès');
      }
      setShowModal(false);
      chargerBoutiques();
      setTimeout(() => setSucces(''), 3000);
    } catch (e: any) {
      setErreur(e.response?.data?.message || 'Une erreur est survenue');
    }
  };

  const supprimer = async () => {
    if (!boutiqueASupprimer) return;
    try {
      await api.delete(`/boutiques/${boutiqueASupprimer.id}`);
      setSucces('Boutique supprimée');
      setShowDeleteModal(false);
      chargerBoutiques();
      setTimeout(() => setSucces(''), 3000);
    } catch (e) {
      setErreur('Erreur lors de la suppression');
    }
  };

  const genererCode = (nom: string) => {
    return nom.split(' ')
      .filter(w => w.length > 0)
      .map(w => w.replace(/[^a-zA-Z]/g, ''))
      .join('')
      .slice(0, 4)
      .toUpperCase();
  };

  const statutBadge = (statut: string) => {
    if (statut === 'active') return <span className="badge badge-green">✓ Active</span>;
    if (statut === 'preparation') return <span className="badge badge-amber">⏳ En préparation</span>;
    return <span className="badge badge-red">✗ Inactive</span>;
  };

  if (chargement) return <div className="page-chargement">Chargement des boutiques...</div>;

  return (
    <div className="boutiques-page">
      <div className="page-header">
        <div>
          <h1>Gestion des boutiques</h1>
          <p>Créer, modifier et gérer vos points de vente</p>
        </div>
        <button className="btn-primary" onClick={ouvrirCreation}>
          + Nouvelle boutique
        </button>
      </div>

      {succes && <div className="alert-succes">✅ {succes}</div>}
      {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

      <div className="stats-bar">
        <div className="stat-item">
          <span className="stat-val">{boutiques.length}</span>
          <span className="stat-lbl">Boutiques au total</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: '#3B6D11' }}>
            {boutiques.filter(b => b.statut === 'active').length}
          </span>
          <span className="stat-lbl">Actives</span>
        </div>
        <div className="stat-item">
          <span className="stat-val" style={{ color: '#185FA5' }}>
            {5 - boutiques.length}
          </span>
          <span className="stat-lbl">Emplacements disponibles</span>
        </div>
      </div>

      <div className="boutiques-grid">
        {boutiques.map(b => (
          <div key={b.id} className="boutique-card">
            <div className="card-banner" style={{ background: b.couleur || '#185FA5' }}>
              <div className="card-banner-content">
                <div>
                  <div className="card-nom">{b.nom}</div>
                  <div className="card-code">{b.code} · {b.ville}</div>
                </div>
                {statutBadge(b.statut)}
              </div>
            </div>
            <div className="card-body">
              <div className="card-info">
                <div className="info-row">📍 {b.adresse}{b.quartier ? `, ${b.quartier}` : ''}</div>
                <div className="info-row">📞 {b.telephone}</div>
                {b.gerant && <div className="info-row">👤 Gérant : <strong>{b.gerant}</strong></div>}
                {b.horaires && <div className="info-row">🕐 {b.horaires}</div>}
              </div>
              <div className="card-options">
                {b.mobileMoneyActif && <span className="opt-badge">📱 Mobile Money</span>}
                {b.creditActif && <span className="opt-badge">💳 Crédit</span>}
                {b.tvaActif && <span className="opt-badge">🧾 TVA</span>}
              </div>
              <div className="card-actions">
                <button className="btn-edit" onClick={() => ouvrirEdition(b)}>✏️ Modifier</button>
                <button className="btn-delete" onClick={() => ouvrirSuppression(b)}>🗑️ Supprimer</button>
              </div>
            </div>
          </div>
        ))}

        {boutiques.length < 5 && (
          <div className="boutique-card card-add" onClick={ouvrirCreation}>
            <div className="add-content">
              <div className="add-icon">+</div>
              <div className="add-text">Nouvelle boutique</div>
              <div className="add-sub">{5 - boutiques.length} emplacement{5 - boutiques.length > 1 ? 's' : ''} disponible{5 - boutiques.length > 1 ? 's' : ''}</div>
            </div>
          </div>
        )}
      </div>

      {/* MODAL CREATION / EDITION */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{boutiqueEnEdition ? `Modifier "${form.nom}"` : 'Nouvelle boutique'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {erreur && <div className="alert-erreur">⚠️ {erreur}</div>}

              <div className="form-section">Identification</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Nom <span className="req">*</span></label>
                  <input value={form.nom} onChange={e => {
                    const nom = e.target.value;
                    setForm(f => ({ ...f, nom, code: boutiqueEnEdition ? f.code : genererCode(nom) }));
                  }} placeholder="Ex: Boutique Patte d'Oie" />
                </div>
                <div className="form-group">
                  <label>Code <span className="req">*</span></label>
                  <input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} maxLength={6} style={{ fontFamily: 'monospace' }} />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Statut</label>
                  <select value={form.statut} onChange={e => setForm(f => ({ ...f, statut: e.target.value }))}>
                    <option value="active">Active</option>
                    <option value="preparation">En préparation</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Couleur</label>
                  <div className="color-grid">
                    {couleurs.map(c => (
                      <div key={c.val} className={`color-opt ${form.couleur === c.val ? 'active' : ''}`}
                        style={{ background: c.val }}
                        onClick={() => setForm(f => ({ ...f, couleur: c.val }))}
                        title={c.label}
                      />
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-section">Localisation</div>
              <div className="form-group">
                <label>Adresse <span className="req">*</span></label>
                <input value={form.adresse} onChange={e => setForm(f => ({ ...f, adresse: e.target.value }))} placeholder="Ex: Avenue Kwamé N'Krumah, Secteur 1" />
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Ville</label>
                  <select value={form.ville} onChange={e => setForm(f => ({ ...f, ville: e.target.value }))}>
                    <option>Ouagadougou</option>
                    <option>Bobo-Dioulasso</option>
                    <option>Koudougou</option>
                    <option>Banfora</option>
                    <option>Ouahigouya</option>
                    <option>Kaya</option>
                    <option>Fada N'Gourma</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Quartier / Secteur</label>
                  <input value={form.quartier} onChange={e => setForm(f => ({ ...f, quartier: e.target.value }))} placeholder="Ex: Zone 1, Patte d'Oie" />
                </div>
              </div>

              <div className="form-section">Contact</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Téléphone <span className="req">*</span></label>
                  <input value={form.telephone} onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))} placeholder="+226 XX XX XX XX" />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="boutique@nexacom.bf" />
                </div>
              </div>

              <div className="form-section">Configuration</div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Gérant</label>
                  <input value={form.gerant} onChange={e => setForm(f => ({ ...f, gerant: e.target.value }))} placeholder="Nom du gérant" />
                </div>
                <div className="form-group">
                  <label>Horaires</label>
                  <input value={form.horaires} onChange={e => setForm(f => ({ ...f, horaires: e.target.value }))} placeholder="Lun-Sam 8h-18h" />
                </div>
              </div>

              <div className="form-section">Options</div>
              <div className="options-grid">
                {[
                  { key: 'mobileMoneyActif', label: 'Mobile Money' },
                  { key: 'creditActif', label: 'Ventes à crédit' },
                  { key: 'tvaActif', label: 'TVA 18%' },
                  { key: 'remiseActif', label: 'Remises manuelles' },
                  { key: 'fideliteActif', label: 'Programme fidélité' },
                ].map(opt => (
                  <label key={opt.key} className="toggle-row">
                    <span>{opt.label}</span>
                    <input type="checkbox" checked={(form as any)[opt.key]}
                      onChange={e => setForm(f => ({ ...f, [opt.key]: e.target.checked }))} />
                  </label>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn-primary" onClick={sauvegarder}>
                {boutiqueEnEdition ? 'Enregistrer les modifications' : 'Créer la boutique'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL SUPPRESSION */}
      {showDeleteModal && boutiqueASupprimer && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header danger">
              <h2>⚠️ Supprimer la boutique</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>Vous êtes sur le point de supprimer <strong>{boutiqueASupprimer.nom}</strong>. Cette action est irréversible.</p>
              <div className="form-group" style={{ marginTop: 16 }}>
                <label>Tapez le nom de la boutique pour confirmer :</label>
                <input value={confirmNom} onChange={e => setConfirmNom(e.target.value)}
                  placeholder={boutiqueASupprimer.nom} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowDeleteModal(false)}>Annuler</button>
              <button className="btn-danger"
                disabled={confirmNom !== boutiqueASupprimer.nom}
                onClick={supprimer}>
                Supprimer définitivement
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}